import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../utils/DataAccessUtils';
import { logoutSuccess } from '../actions/session_actions';
import {
	fetchAllMessagesError,
	fetchAllMessagesStart,
	fetchAllMessagesSuccess,
} from '../actions/messages_actions';
import { PROJECTS_CRUD_ACTION_CREATORS } from '../actions/projects_actions';
import { normalizeNotesResponse } from './messages_thunks';

const fetchUtils = new FetchUtils();

// Project notes live in the shared `state.messages` chat store, the same place
// work-order and walkthrough notes live, so the notes UI works unchanged.
//
// That store is keyed by a bare channel id, and work-order notes key theirs by the
// raw work order id — so project 5 and work order 5 would share a channel. Hence
// the prefix: it keeps a project's notes in their own channel.
//
// The id must never contain a period: consumers read the channel with
// nullSafeGet(`${channelId}.messages`, ...), which splits the path on '.'.
export const projectNotesChannelId = (projectId) => `project-${projectId}`;

const projectNotesUrl = (roleType) => `/api/v1/${roleType}/project/project_notes`;

const fetchProjectNotesForRole = (roleType) => (projectId) => {
	const channelId = projectNotesChannelId(projectId);
	// no_pagination: a project's note history is the chat transcript — all of it, in
	// one go. The API sorts newest first; normalizeNotesResponse flips it to oldest
	// first, which is the order the chat view scrolls through.
	const apiUrl = `${projectNotesUrl(roleType)}?projectId=${projectId}&no_pagination=true`;
	return (dispatch) =>
		new Promise((resolve, reject) => {
			dispatch(fetchAllMessagesStart(channelId));
			fetchUtils
				.get(getBackendUri() + apiUrl, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				})
				.then(parseJSON)
				.then(({ json, status }) => {
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (json.status === 'error') {
						dispatch(fetchAllMessagesError(json.message, channelId));
						reject(json.message);
					} else {
						const notes = normalizeNotesResponse(json.data);
						dispatch(fetchAllMessagesSuccess(channelId, notes));
						resolve(notes);
					}
				})
				.catch((error) => {
					dispatch(fetchAllMessagesError(error.message, channelId));
					reject(error.message);
				});
		});
};

// One note at a time, which is what the chat input sends. The note entity is built
// by CommonNoteInputForm (text / photo / video / audio / elements); noteAddedBy and
// noteAddedAt are server-stamped from the caller, so whatever the form put there is
// ignored.
//
// The response is a ProjectNoteAppendResponse — the work-order append_notes analogue:
// { notes: [...the project's whole refreshed thread, hydrated...], project: <subscriber
// delta> }. Both halves matter: mentions in a note subscribe the mentioned users, so the
// delta is how the subscriber chips stay current without a follow-up GET.
const appendProjectNoteForRole = (roleType) => (projectId, note) => {
	const channelId = projectNotesChannelId(projectId);
	return (dispatch) =>
		new Promise((resolve, reject) => {
			fetchUtils
				.post(getBackendUri() + projectNotesUrl(roleType), {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					data: { ...note, projectId: Number(projectId) },
				})
				.then(parseJSON)
				.then(({ json, status }) => {
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (json.status === 'error') {
						reject(json.message);
					} else {
						const notes = normalizeNotesResponse(nullSafeGetOrElse('data.notes', json, []));
						dispatch(fetchAllMessagesSuccess(channelId, notes));
						// updatePartialSuccess, not updateSuccess: the delta carries only the id and
						// the four subscriber fields, so a full update would replace the stored
						// project with a stub and drop its title, dates and metrics.
						const subscriberDelta = nullSafeGet('data.project', json);
						if (subscriberDelta) {
							dispatch(PROJECTS_CRUD_ACTION_CREATORS.updatePartialSuccess(subscriberDelta));
						}
						resolve(notes);
					}
				})
				.catch((error) => reject(error.message));
		});
};

// Role factories rather than pre-bound ForBuyer/ForSupplier pairs: the caller passes
// its own userType, so there is no per-call-site ternary to pick the wrong one.
export const fetchProjectNotes = fetchProjectNotesForRole;
export const appendProjectNote = appendProjectNoteForRole;
